import {
    Directive, ElementRef, Renderer2, Input,
    AfterViewInit, OnChanges, OnDestroy, SimpleChanges,
    Inject, PLATFORM_ID,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

@Directive({ selector: '[sticky]', standalone: false })
export class StickyColumnDirective implements AfterViewInit, OnChanges, OnDestroy {
    @Input() stickyWidth?: number;
    @Input() stickyMobileWidth?: number;
    @Input() stickyRight: boolean = false;
    @Input() stickyDisabled: boolean = false;

    isRightmost: boolean = false;
    isLeftmost: boolean  = false;

    /** The scrollable (non-sticky) section must keep at least this many px visible.
     *  If pinning the sticky columns would drop below it, EVERY sticky column
     *  (left and right) is released so the whole table scrolls. */
    private static readonly MIN_SCROLLABLE_VISIBLE = 40;

    private baseZIndex?: number;
    pinned = false;
    private resizeObserver?: ResizeObserver;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: Object,
        public el: ElementRef,
        private renderer: Renderer2
    ) {}

    ngAfterViewInit() { this.apply(); this.observeResize(); }
    ngOnChanges(changes: SimpleChanges) { this.apply(); }
    ngOnDestroy() { this.resizeObserver?.disconnect(); }

    private observeResize() {
        if (!isPlatformBrowser(this.platformId)) return;
        // observe the scroll viewport, not the table: its width tracks the window,
        // so the release/pin decision re-runs when the screen shrinks even after the
        // table itself has stopped shrinking (overflowing).
        const scroller =
            (this.el.nativeElement.closest('.table-container') as HTMLElement) ??
            (this.el.nativeElement.closest('[data-scroll-container]') as HTMLElement) ??
            (this.el.nativeElement.closest('table') as HTMLElement);
        if (!scroller) return;
        this.resizeObserver = new ResizeObserver(() => this.recalculateAll());
        this.resizeObserver.observe(scroller);
    }

    private recalculateAll() {
        const table = (this.el.nativeElement as HTMLElement).closest('table');
        if (!table) return;
        const cells = Array.from(table.querySelectorAll('[sticky]')) as HTMLElement[];
        cells.forEach(c => (c as any).__stickyDirective?.applyWidth());
        cells.forEach(c => (c as any).__stickyDirective?.applyStickyMode());
        cells.forEach(c => (c as any).__stickyDirective?.calculatePosition());
        cells.forEach(c => (c as any).__stickyDirective?.calculateEdge());
    }

    private apply() {
        if (!isPlatformBrowser(this.platformId)) return;

        const cell = this.el.nativeElement as HTMLElement;
        (cell as any).__stickyDirective = this;

        if (this.stickyWidth === undefined) {
            this.renderer.removeAttribute(this.el.nativeElement, 'sticky');
            return;
        }
        // capture the CSS z-index once, before any inline z-index is written
        if (this.baseZIndex === undefined) {
            const z = parseInt(getComputedStyle(cell).zIndex, 10);
            this.baseZIndex = isNaN(z) ? 0 : z;
        }
        setTimeout(() => {
            this.applyWidth();
            this.applyStickyMode();
            this.calculatePosition();
            this.calculateEdge();
        });
    }

    // ---- flag-based sibling classification (order-independent) ----

    private dirOf(s: HTMLElement): StickyColumnDirective | undefined {
        return (s as any).__stickyDirective;
    }

    private isLeftSticky(s: HTMLElement): boolean {
        const d = this.dirOf(s);
        return !!d && d.stickyWidth !== undefined && !d.stickyRight;
    }

    private isRightSticky(s: HTMLElement): boolean {
        const d = this.dirOf(s);
        return !!d && d.stickyWidth !== undefined && d.stickyRight;
    }

    // ---- sticky vs scroll-over ----

    /** Visible width the table is laid out in (the scrolling viewport, not the full table). */
    private availableWidth(): number {
        const cell = this.el.nativeElement as HTMLElement;
        const scroller =
            (cell.closest('.table-container') as HTMLElement) ??
            (cell.closest('[data-scroll-container]') as HTMLElement) ??
            (cell.closest('table')?.parentElement as HTMLElement);
        return scroller?.clientWidth ?? 0;
    }

    /** True when, with ALL sticky columns pinned, the scrollable section still has
     *  usable visible room. False -> release EVERY sticky column (incl. right) so
     *  the whole table scrolls. */
    private columnsFit(): boolean {
        const avail = this.availableWidth();
        if (!avail) return true;

        const row = (this.el.nativeElement as HTMLElement).parentElement;
        if (!row) return true;

        const stickyTotal = (Array.from(row.children) as HTMLElement[])
            .map(s => this.dirOf(s))
            .filter((d): d is StickyColumnDirective => !!d && d.stickyWidth !== undefined && !d.stickyDisabled)
            .reduce((sum, d) => sum + (d.stickyWidth ?? 0), 0);

        return avail - stickyTotal >= StickyColumnDirective.MIN_SCROLLABLE_VISIBLE;
    }

    applyWidth() {
      if (this.stickyWidth === undefined) return;
      const cell = this.el.nativeElement as HTMLElement;
      const isMobile = window.innerWidth < 768;
      const width = (isMobile && this.stickyMobileWidth !== undefined)
        ? this.stickyMobileWidth
        : this.stickyWidth;
      cell.style.width = cell.style.minWidth = `${width}px`;
    }
    /** Pin the column, or release it (when tight) so the whole table scrolls. */
    applyStickyMode() {
        if (this.stickyDisabled) {
          const cell = this.el.nativeElement as HTMLElement;
          cell.style.position = '';
          cell.style.left = '';
          cell.style.zIndex = '';
          this.pinned = false;
          return;
        }
        if (this.stickyWidth === undefined) return;
        if (!isPlatformBrowser(this.platformId)) return;
        const cell = this.el.nativeElement as HTMLElement;

        // sticky-right stays pinned always; left columns release when tight
        const keepPinned = this.stickyRight || this.columnsFit();
        this.pinned = keepPinned;

        if (keepPinned) {
            cell.style.position = 'sticky';
            cell.style.zIndex = `${(this.baseZIndex ?? 0) + 1}`;
        } else {
            // release HORIZONTALLY only: keep position:sticky so the stylesheet's
            // `thead th { top: 0 }` still pins the header to the top on vertical scroll;
            // left/right:auto means no horizontal pinning, so it scrolls sideways.
            cell.style.position = 'sticky';
            cell.style.left = 'auto';
            cell.style.right = 'auto';
            cell.style.zIndex = '';
        }
    }

    calculateEdge() {
      const cell  = this.el.nativeElement as HTMLElement;
      const row   = cell.parentElement;
      if (!row) return;

      const siblings  = Array.from(row.children) as HTMLElement[];
      const cellIndex = siblings.indexOf(cell);

      if (this.stickyRight) {
        this.isLeftmost = !siblings.slice(0, cellIndex).some(s => this.isRightSticky(s));
      } else {
        this.isRightmost = !siblings.slice(cellIndex + 1).some(s =>
          this.isLeftSticky(s) && !(s as any).__stickyDirective?.stickyDisabled
        );
      }
    }

    calculatePosition() {
        if (!isPlatformBrowser(this.platformId)) return;
        // released cells are sticky-for-top only; don't give them horizontal offsets
        if (!this.pinned) return;
        const cell = this.el.nativeElement as HTMLElement;

        const row  = cell.parentElement;
        if (!row) return;

        const siblings  = Array.from(row.children) as HTMLElement[];
        const cellIndex = siblings.indexOf(cell);

        if (this.stickyRight) {
            let right = 0;
            for (const sib of siblings.slice(cellIndex + 1)) {
                if (this.isRightSticky(sib)) right += sib.offsetWidth;
            }
            cell.style.right = `${right}px`;
            cell.style.left  = '';
        } else {
            let left = 0;
            for (const sib of siblings.slice(0, cellIndex)) {
                if (this.isLeftSticky(sib)) left += sib.offsetWidth;
            }
            cell.style.left  = `${left}px`;
            cell.style.right = '';
        }
    }
}
